import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { LanguageSchema } from '../common/language.js';
import { CurrencyCodeSchema } from '../common/currency.js';
import { IanaZoneSchema } from '../common/zone.js';
import { WeekStartSchema } from '../common/period.js';
// Request schemas for /api/projects/:projectId/* (the member surface).
// The sysadmin platform surface keeps its own file (platform-requests.ts).
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
// PATCH /api/projects/:projectId/settings — the project_admin self-service slice
// of `projects` (gate: sysadmin OR project_admin; no capability key — CLAUDE.md
// classifies `manage_project_settings` as an admin built-in).
//
// Every field optional; PRESENT ⇒ change (the platform-requests convention).
// The commercial columns a project_admin must NOT move are simply absent from
// this schema, so `.strict()` rejects them outright rather than silently
// dropping: `type`, `status`, `is_trial`, `trial_expires_at`, the `has_*`
// entitlement flags, seats, and `countries`. `name` (the code) is immutable
// everywhere. `mother_company`/`_address` belong to the billing-info surface.
//
// The zone and the week start are the project's own (ADR-082 §3): a zone sent
// here always wins; a country change sent WITHOUT a zone re-derives the zone from
// the new country (never to nothing — a country without a zone keeps the current
// one); clearing the country keeps the zone. The drawer sends both as the admin
// picks; the rule guards partial requests (a script). The zone is never null and
// never '' — a project always has one.
export const UpdateProjectSettingsRequestSchema = z
    .object({
    ...stampedBody,
    display_name: z.string().trim().min(1).max(64).optional(),
    description: z.string().trim().max(500).optional(),
    color_primary: z.string().regex(HEX_COLOR).optional(),
    color_on_primary: z.string().regex(HEX_COLOR).optional(),
    default_country_iso_code: z.string().length(2).nullable().optional(),
    timezone_iana: IanaZoneSchema.optional(),
    week_start: WeekStartSchema.optional(),
    // The project's base currency — the DEFAULT of every money field, never a rule (common/currency): set
    // EXPLICITLY, never re-derived from a country change (a default of money is changed on purpose).
    default_currency: CurrencyCodeSchema.optional(),
    default_language: LanguageSchema.optional(),
    use_gateway_by_default: z.boolean().optional(),
    // Seconds between site check-ins. Floor 30s guards a self-inflicted DoS via
    // the polling fleet; ceiling 86400 is one day (the column is plain integer).
    default_polling_rate_s: z.number().int().min(30).max(86_400).nullable().optional(),
    // The Files review policy (ADR-068 §2 — four switches in the admin's words)
    // + the two numbers; live server-side (the inbox predicate, the reasons, the
    // bin's purge date re-read them per request).
    files_review_undescribed: z.boolean().optional(),
    files_review_off_site: z.boolean().optional(),
    files_review_no_position: z.boolean().optional(),
    files_trust_technicians: z.boolean().optional(),
    visit_gps_tolerance_m: z.number().int().min(10).max(10_000).optional(),
    visit_nearby_radius_m: z.number().int().min(100).max(100_000).optional(),
    visit_expiry_hours: z.number().int().min(1).max(48).optional(),
    files_bin_grace_days: z.number().int().min(1).max(365).optional(),
    // The TICKET settings (ADR-073; the Field work page): the capture's filing
    // grace — days, or null = never (someone closes each ticket by hand) — and
    // the dormancy threshold. Both read live server-side.
    ticket_capture_grace_days: z.number().int().min(1).max(365).nullable().optional(),
    ticket_dormant_hours: z.number().int().min(1).max(720).optional(),
})
    .strict();
